import React, { useEffect, useState } from 'react';
import { EyeOff, Loader2, Lock } from 'lucide-react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BLOCK_KEYS = new Set(['p', 's', 'c', 'u', 'a']);

/** Visualizador protegido: páginas renderizadas no servidor com marca d'água; sem baixar/salvar/imprimir/copiar.
 * Obs.: nenhum navegador impede 100% capturas — a marca d'água identifica o responsável. */
export function ProtectedViewer({ docId, onClose, onOpened }) {
  const [meta, setMeta] = useState(null);
  const [pages, setPages] = useState({});
  const [hidden, setHidden] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!docId) return undefined;
    let urls = [];
    setMeta(null); setPages({}); setErr(null);
    api.get(`/dna-mapa/inclusao/documentos/${docId}/visualizador`).then(async ({ data }) => {
      setMeta(data); onOpened?.();
      for (let i = 1; i <= data.pages; i += 1) {
        const r = await api.get(`/dna-mapa/inclusao/documentos/${docId}/pagina/${i}`, { responseType: 'blob' });
        const u = URL.createObjectURL(r.data); urls.push(u); setPages((p) => ({ ...p, [i]: u }));
      }
    }).catch((e) => setErr(e?.response?.data?.detail || 'Sem autorização.'));
    const key = (e) => {
      if (e.key === 'PrintScreen') { setHidden(true); navigator.clipboard?.writeText('').catch(() => {}); }
      if ((e.ctrlKey || e.metaKey) && BLOCK_KEYS.has(e.key.toLowerCase())) { e.preventDefault(); e.stopPropagation(); }
    };
    const blur = () => setHidden(true);
    const vis = () => document.hidden && setHidden(true);
    window.addEventListener('keydown', key, true); window.addEventListener('keyup', key, true);
    window.addEventListener('blur', blur); document.addEventListener('visibilitychange', vis);
    const before = () => setHidden(true);
    window.addEventListener('beforeprint', before);
    document.body.classList.add('dna-no-print');
    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u)); urls = [];
      window.removeEventListener('keydown', key, true); window.removeEventListener('keyup', key, true);
      window.removeEventListener('blur', blur); document.removeEventListener('visibilitychange', vis);
      window.removeEventListener('beforeprint', before); document.body.classList.remove('dna-no-print');
    };
  }, [docId]); // eslint-disable-line react-hooks/exhaustive-deps
  const stop = (e) => e.preventDefault();
  return (
    <Dialog open={!!docId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[94vh] max-w-4xl overflow-hidden p-0" data-testid="inc-viewer" onContextMenu={stop}>
        <style>{'@media print { body.dna-no-print * { display: none !important; } body.dna-no-print::after { content: "Impressão não permitida — documento confidencial."; display: block; } }'}</style>
        <DialogHeader className="border-b px-5 py-3 text-left">
          <DialogTitle className="flex items-center gap-2 text-base"><Lock className="h-4 w-4 text-[#D6336C]" /> {meta?.label || 'Documento'} {meta ? `· v${meta.version}` : ''}</DialogTitle>
          <DialogDescription>Somente visualização. Download, impressão e cópia bloqueados; páginas identificadas com seu nome. Acesso auditado.</DialogDescription>
        </DialogHeader>
        <div className="relative max-h-[80vh] select-none overflow-y-auto bg-[#2A1240] p-4" style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
          onContextMenu={stop} onDragStart={stop} onCopy={stop} onCut={stop} onMouseMove={() => hidden && setHidden(false)} data-testid="inc-viewer-paginas">
          {err && <p className="p-6 text-center text-sm text-white" data-testid="inc-viewer-erro">{err}</p>}
          {!meta && !err && <p className="flex items-center justify-center gap-2 p-10 text-sm text-white/80"><Loader2 className="h-4 w-4 animate-spin" /> Preparando visualização protegida…</p>}
          {meta && Array.from({ length: meta.pages }, (_, i) => i + 1).map((n) => (
            <div key={n} className="mx-auto mb-4 max-w-[820px] bg-white shadow-lg">
              {pages[n] ? <img src={pages[n]} alt={`Página ${n}`} draggable={false} className="pointer-events-none block w-full" style={{ filter: hidden ? 'blur(18px)' : 'none' }} data-testid={`inc-viewer-pagina-${n}`} />
                : <div className="flex h-64 items-center justify-center text-sm text-gray-400"><Loader2 className="h-4 w-4 animate-spin" /></div>}
            </div>
          ))}
          {hidden && <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-[#2A1240]/85 text-white" data-testid="inc-viewer-oculto"><span className="flex items-center gap-2 text-sm font-semibold"><EyeOff className="h-5 w-5" /> Conteúdo ocultado. Mova o mouse sobre a página para continuar.</span></div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
